import React, { useState, useMemo } from "react";

/**
 * Modal affichant le bilan comparatif d'un élève entre EvalNat et EvalBilan
 * Affiche les deux résultats côte à côte
 */
function ResultatComparatifByEleve({ show, onClose, eleveNat, eleveBilan,
     EvalNat, evalBilan, testSpecifique }) {

        
    const [activeTab, setActiveTab] = useState(null);

    // Extraire prénom et nom depuis nom_eleve (format "NOM Prénom")
    const { prenom, nom, initial } = useMemo(() => {
        const eleve = eleveNat || eleveBilan;
        if (!eleve?.nom_eleve) return { prenom: '', nom: '', initial: '?' };
        const parts = eleve.nom_eleve.split(' ');
        const nomPart = parts[0] || '';
        const prenomPart = parts.slice(1).join(' ') || '';
        return {
            nom: nomPart,
            prenom: prenomPart,
            initial: nomPart.charAt(0).toUpperCase()
        };
    }, [eleveNat, eleveBilan]);

    // Générer une couleur basée sur le nom
    const avatarColor = useMemo(() => {
        const eleve = eleveNat || eleveBilan;
        if (!eleve?.nom_eleve) return '#0EA5E9';
        const colors = ['#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#6366F1'];
        let hash = 0;
        for (let i = 0; i < eleve.nom_eleve.length; i++) {
            hash = eleve.nom_eleve.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    }, [eleveNat, eleveBilan]);

    // Déterminer les catégories disponibles selon la matière
    const categories = useMemo(() => {
        if (!EvalNat) return [];
        const matiere = EvalNat.matiere;
        const niveau = EvalNat.classe_nom?.toString()[0];

        if (matiere === 'maths') {
            const cats = [{ key: 'Automatismes', label: '📊 Automatismes' }];
            if (niveau === '6' || niveau === '4') {
                cats.push({ key: 'Problèmes', label: '📋 Problèmes' });
            }
            return cats;
        } else if (matiere === 'français' || matiere === 'Français') {
            switch (niveau) {
                case '6':
                case '4':
                    return [
                        { key: 'Lexique', label: '📚 Lexique' },
                        { key: 'ComprehensionEcrite', label: '📖 Compréhension écrite' }
                    ];
                case '5':
                    return [{ key: 'CompetencesLangagieres', label: '💬 Compétences langagières' }];
                default:
                    return [{ key: 'Lexique', label: '📚 Lexique' }];
            }
        }
        return [];
    }, [EvalNat]);

    // Initialiser l'onglet actif avec testSpecifique ou la première catégorie (seulement à l'ouverture)
    React.useEffect(() => {
        // Ne s'exécute que si activeTab est null (première ouverture)
        if (activeTab === null) {
            if (testSpecifique && categories.find(c => c.key === testSpecifique)) {
                setActiveTab(testSpecifique);
            } else if (categories.length > 0) {
                setActiveTab(categories[0].key);
            }
        }
    }, [categories, testSpecifique, activeTab]);

    // Mapper les réponses de l'élève EvalNat
    const eleveReponsesNat = useMemo(() => {
        if (!eleveNat?.reponses || !EvalNat?.questions || !activeTab) return [];

        const typeLabelsMap = {
            'Automatismes': 'Automatisme',
            'Problèmes': 'Résolution de problèmes',
            'Lexique': 'Lexique',
            'CompetencesLangagieres': 'Compétences langagières et linguistiques',
            'ComprehensionEcrite': "Compréhension de l'écrit"
        };

        const typeLabel = typeLabelsMap[activeTab];
        const questionsCategorie = EvalNat.questions.filter(q => {
            if (activeTab === 'ComprehensionEcrite') {
                return q.type_label?.startsWith("Compréhension de l'écrit");
            }
            return q.type_label === typeLabel;
        });

        const result = [];

        if (activeTab === 'ComprehensionEcrite') {
            // Extraire dynamiquement les catégories depuis les questions EvalNat
            const comprehensionCategories = [...new Set(
                questionsCategorie.map(q => {
                    let name = q.type_label.replace("Compréhension de l'écrit ", "");
                    if (name.startsWith("Document ")) name = name.replace("Document ", "");
                    return name;
                })
            )];
            comprehensionCategories.forEach(category => {
                const reponsesCategorie = eleveNat.reponses[category];
                if (!reponsesCategorie) return;

                Object.entries(reponsesCategorie).forEach(([questionNum, reponseData]) => {
                    const questionKey = `${category} ${questionNum}`;
                    const question = questionsCategorie.find(q => {
                        let texteName = q.type_label.replace("Compréhension de l'écrit ", "");
                        if (texteName.startsWith("Document ")) {
                            texteName = texteName.replace("Document ", "");
                        }
                        const cleComposite = `${texteName} ${q.question_num}`;
                        return cleComposite === questionKey;
                    });

                    if (question) {
                        result.push({
                            questionId: questionKey,
                            questionLabel: question.nom_label || question.texte || 'Question',
                            reponse: reponseData.réponse || reponseData.reponse || '-',
                            bonneReponse: question.bonne_reponse || '-',
                            isCorrect: reponseData.score === 1,
                            score: reponseData.score
                        });
                    }
                });
            });
        } else if (activeTab === 'CompetencesLangagieres') {
            const reponsesCategorie = eleveNat.reponses.Compétence;
            if (reponsesCategorie) {
                Object.entries(reponsesCategorie).forEach(([questionNum, reponseData]) => {
                    const question = questionsCategorie.find(q => q.question_num === questionNum);
                    if (question) {
                        result.push({
                            questionId: questionNum,
                            questionLabel: question.nom_label || question.texte || 'Question',
                            reponse: reponseData.réponse || reponseData.reponse || '-',
                            bonneReponse: question.bonne_reponse || '-',
                            isCorrect: reponseData.score === 1,
                            score: reponseData.score
                        });
                    }
                });
            }
        } else {
            const reponsesCategorie = eleveNat.reponses[activeTab];
            if (reponsesCategorie) {
                Object.entries(reponsesCategorie).forEach(([questionNum, reponseData]) => {
                    const question = questionsCategorie.find(q => q.question_num === questionNum);
                    if (question) {
                        result.push({
                            questionId: questionNum,
                            questionLabel: question.nom_label || question.texte || 'Question',
                            reponse: reponseData.réponse || reponseData.reponse || '-',
                            bonneReponse: question.bonne_reponse || '-',
                            isCorrect: reponseData.score === 1,
                            score: reponseData.score
                        });
                    }
                });
            }
        }

        result.sort((a, b) => {
            const extractNum = (key) => {
                const match = key.match(/Q(\d+)/i);
                return match ? parseInt(match[1], 10) : 0;
            };
            return extractNum(a.questionId) - extractNum(b.questionId);
        });

        return result;
    }, [eleveNat, EvalNat, activeTab]);

    // Mapper les réponses de l'élève EvalBilan
    const eleveReponsesBilan = useMemo(() => {
        if (!eleveBilan?.reponses || !evalBilan?.questions || !activeTab) return [];

        // Mapping des clés UI vers les clés dans eleveBilan.reponses
        const categoriesBilanMap = {
            'Automatismes': 'Automatisme',
            'Problèmes': 'Résolution de problèmes',
            'Lexique': 'Lexique',
            'CompetencesLangagieres': 'Compétences langagières et linguistiques',
            'ComprehensionEcrite': "Compréhension de l'écrit"
        };

        const typeLabel = categoriesBilanMap[activeTab];
        const categorieBilan = categoriesBilanMap[activeTab];

        // Filtrer les questions du bilan selon la catégorie
        const questionsBilan = evalBilan.questions.filter(q => {
            if (activeTab === 'ComprehensionEcrite') {
                return q.type_label?.startsWith("Compréhension de l'écrit");
            }
            return q.type_label === typeLabel;
        });

        const result = [];

        // Pour ComprehensionEcrite, les réponses sont réparties sur plusieurs clés
        if (activeTab === 'ComprehensionEcrite') {
            questionsBilan.forEach(question => {
                // Chercher la réponse dans toutes les clés commençant par "Compréhension de l'écrit"
                const matchingKey = Object.keys(eleveBilan.reponses).find(key =>
                    key.startsWith("Compréhension de l'écrit") && eleveBilan.reponses[key][question.id]
                );
                if (matchingKey) {
                    const reponseEleve = eleveBilan.reponses[matchingKey][question.id];
                    const isCorrect = reponseEleve.score === 1;
                    // Extraire le nom du texte pour créer un ID composite "Arachné Q1"
                    let texteName = matchingKey.replace("Compréhension de l'écrit ", "");
                    if (texteName.startsWith("Document ")) texteName = texteName.replace("Document ", "");
                    const qNum = question.question_num || `Q${questionsBilan.indexOf(question) + 1}`;
                    result.push({
                        questionId: `${texteName} ${qNum}`,
                        questionLabel: question.nom_label || question.texte || 'Question',
                        reponse: reponseEleve.réponse || reponseEleve.reponse || '-',
                        bonneReponse: question.bonne_reponse || '-',
                        isCorrect: isCorrect,
                        score: reponseEleve.score
                    });
                }
            });
        } else {
            // Cas normal : une seule clé de catégorie
            const reponsesCategorieEleve = eleveBilan.reponses[categorieBilan];
            if (!reponsesCategorieEleve) return result;

            questionsBilan.forEach(question => {
                const reponseEleve = reponsesCategorieEleve[question.id];
                if (reponseEleve !== undefined) {
                    const isCorrect = reponseEleve.score === 1;
                    result.push({
                        questionId: question.question_num || `Q${questionsBilan.indexOf(question) + 1}`,
                        questionLabel: question.nom_label || question.texte || 'Question',
                        reponse: reponseEleve.réponse || reponseEleve.reponse || '-',
                        bonneReponse: question.bonne_reponse || '-',
                        isCorrect: isCorrect,
                        score: reponseEleve.score
                    });
                }
            });
        }

        // Trier par numéro de question
        result.sort((a, b) => {
            const extractNum = (key) => {
                const match = String(key).match(/Q?(\d+)/i);
                return match ? parseInt(match[1], 10) : 0;
            };
            return extractNum(a.questionId) - extractNum(b.questionId);
        });

        return result;
    }, [eleveBilan, evalBilan, activeTab]);

    // Calculer les stats pour EvalNat
    const statsNat = useMemo(() => {
        if (!eleveNat?.reponses) return { reussites: 0, echecs: 0, total: 0, score: 0 };

        let reussites = 0;
        let echecs = 0;

        Object.entries(eleveNat.reponses).forEach(([key, value]) => {
            if (typeof value !== 'object' || typeof value === 'string') return;
            Object.values(value).forEach(reponse => {
                if (typeof reponse === 'object' && 'score' in reponse) {
                    if (reponse.score === 1) reussites++;
                    else echecs++;
                }
            });
        });

        const total = reussites + echecs;
        const score = total > 0 ? Math.round((reussites / total) * 100) : 0;

        return { reussites, echecs, total, score };
    }, [eleveNat]);

    // Calculer les stats pour EvalBilan
    const statsBilan = useMemo(() => {
        if (!eleveBilan?.reponses) return { reussites: 0, echecs: 0, total: 0, score: 0 };

        let reussites = 0;
        let echecs = 0;

        // Parcourir les catégories (Automatisme, Résolution de problèmes, etc.)
        Object.entries(eleveBilan.reponses).forEach(([, questionsReponses]) => {
            if (typeof questionsReponses !== 'object') return;

            // Parcourir les réponses de chaque question
            Object.values(questionsReponses).forEach(reponse => {
                if (typeof reponse === 'object' && 'score' in reponse) {
                    if (reponse.score === 1) reussites++;
                    else echecs++;
                }
            });
        });

        const total = reussites + echecs;
        const score = total > 0 ? Math.round((reussites / total) * 100) : 0;

        return { reussites, echecs, total, score };
    }, [eleveBilan]);

    const getScoreColor = (score) => {
        if (score >= 70) return '#10B981';
        if (score >= 50) return '#F59E0B';
        return '#EF4444';
    };

    if (!show || (!eleveNat && !eleveBilan)) return null;

    // Composant pour afficher une colonne de résultats
    const ResultColumn = ({ title, color, stats, reponses, isEmpty }) => (
        <div className="flex-1 min-w-0 flex flex-col">
            {/* Header colonne */}
            <div className={`${color} p-2 sm:p-3 text-center`}>
                <h3 className="text-white font-bold text-xs sm:text-sm">{title}</h3>
                <div className="flex justify-center gap-2 sm:gap-3 mt-1 sm:mt-2">
                    <span className="bg-white/20 px-2 py-0.5 rounded text-white text-[10px] sm:text-xs">
                        ✅ {stats.reussites}
                    </span>
                    <span className="bg-white/20 px-2 py-0.5 rounded text-white text-[10px] sm:text-xs">
                        ❌ {stats.echecs}
                    </span>
                    <span
                        className="bg-white px-2 py-0.5 rounded font-bold text-[10px] sm:text-xs"
                        style={{ color: getScoreColor(stats.score) }}
                    >
                        {stats.score}%
                    </span>
                </div>
            </div>

            {/* Liste des réponses */}
            <div className="flex-1 p-2 sm:p-3 bg-slate-50">
                {isEmpty ? (
                    <div className="text-center py-4 text-slate-400 text-xs sm:text-sm">
                        Pas de données
                    </div>
                ) : reponses.length === 0 ? (
                    <div className="text-center py-4 text-slate-400 text-xs sm:text-sm">
                        Aucune réponse dans cette catégorie
                    </div>
                ) : (
                    <div className="flex flex-col gap-1.5 sm:gap-2">
                        {reponses.map((reponse) => (
                            <div
                                key={reponse.questionId}
                                className={`flex items-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 rounded-lg text-[10px] sm:text-xs ${
                                    reponse.isCorrect
                                        ? 'bg-emerald-50 border border-emerald-200'
                                        : 'bg-red-50 border border-red-200'
                                }`}
                            >
                                <div className={`rounded px-1.5 py-0.5 text-[9px] sm:text-[10px] font-bold text-white min-w-[28px] sm:min-w-[32px] text-center ${
                                    reponse.isCorrect ? 'bg-emerald-500' : 'bg-red-500'
                                }`}>
                                    {typeof reponse.questionId === 'number' ? `Q${reponses.indexOf(reponse) + 1}` : reponse.questionId}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-slate-600 truncate leading-tight">
                                        {reponse.questionLabel}
                                    </div>
                                    <div className="flex items-center gap-1 mt-0.5">
                                        <span className={`font-semibold px-1 rounded ${
                                            reponse.isCorrect
                                                ? 'text-emerald-600 bg-emerald-100'
                                                : 'text-red-600 bg-red-100'
                                        }`}>
                                            {reponse.reponse}
                                        </span>
                                        {!reponse.isCorrect && (
                                            <>
                                                <span className="text-slate-300">→</span>
                                                <span className="font-semibold text-emerald-600 bg-emerald-100 px-1 rounded">
                                                    {reponse.bonneReponse}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-white text-[10px] sm:text-xs font-bold flex-shrink-0 ${
                                    reponse.isCorrect ? 'bg-emerald-500' : 'bg-red-500'
                                }`}>
                                    {reponse.isCorrect ? '✓' : '✗'}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <>
            {/* Overlay */}
            <div
                onClick={onClose}
                className="fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center animate-fadeIn"
            >
                {/* Modal Content - plus large pour 2 colonnes */}
                <div
                    onClick={(e) => e.stopPropagation()}
                    className="bg-white rounded-2xl w-[98%] sm:w-[95%] max-w-[1200px] max-h-[90vh] overflow-hidden shadow-2xl animate-slideUp flex flex-col"
                >
                    {/* Header Modal */}
                    <div className="bg-gradient-to-r from-sky-500 via-cyan-500 to-teal-500 p-3 sm:p-4 flex items-center gap-2 sm:gap-3 relative">
                        {/* Bouton fermer */}
                        <button
                            onClick={onClose}
                            className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-white/20 hover:bg-white/30 border-none rounded-lg w-7 h-7 sm:w-8 sm:h-8 cursor-pointer flex items-center justify-center transition-colors"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                                <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </button>

                        {/* Avatar */}
                        <div
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg border-2 border-white/30 flex-shrink-0"
                            style={{ backgroundColor: avatarColor }}
                        >
                            {initial}
                        </div>

                        {/* Infos élève */}
                        <div className="flex-1 min-w-0">
                            <h2 className="text-white m-0 text-base sm:text-lg font-semibold truncate">
                                {prenom} {nom}
                            </h2>
                            <p className="text-white/80 mt-0.5 text-xs truncate">
                                Comparaison Éval Nat vs QCM Suivi • {eleveNat?.classe_nom || eleveBilan?.classe_nom}
                            </p>
                        </div>
                    </div>

                    {/* Onglets catégories */}
                    {categories.length > 0 && (
                        <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-slate-200 bg-slate-50">
                            <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1">
                                {categories.map(cat => {
                                    const isActive = activeTab === cat.key;
                                    return (
                                        <button
                                            key={cat.key}
                                            onClick={() => setActiveTab(cat.key)}
                                            className={`flex items-center gap-1 px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-lg border-none cursor-pointer text-[10px] sm:text-xs font-bold whitespace-nowrap transition-all duration-300 ${
                                                isActive
                                                    ? 'bg-gradient-to-r from-sky-500 via-cyan-500 to-teal-500 text-white shadow-lg shadow-sky-500/40'
                                                    : 'bg-white text-slate-500 shadow-sm hover:text-sky-500 hover:bg-sky-50'
                                            }`}
                                        >
                                            {cat.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Contenu - 2 colonnes avec scroll unifié */}
                    <div className="flex-1 flex overflow-y-auto min-h-0">
                        {/* Colonne EvalNat */}
                        <ResultColumn
                            title="Évaluation Nationale"
                            color="bg-gradient-to-r from-blue-500 to-cyan-500"
                            stats={statsNat}
                            reponses={eleveReponsesNat}
                            isEmpty={!eleveNat}
                        />

                        {/* Séparateur */}
                        <div className="w-px bg-slate-200" />

                        {/* Colonne EvalBilan */}
                        <ResultColumn
                            title="QCM Suivi"
                            color="bg-gradient-to-r from-orange-500 to-amber-500"
                            stats={statsBilan}
                            reponses={eleveReponsesBilan}
                            isEmpty={!eleveBilan}
                        />
                    </div>

                    {/* Footer */}
                    <div className="p-3 sm:p-4 border-t border-slate-200 bg-slate-50">
                        <button
                            onClick={onClose}
                            className="w-full bg-orange-500 hover:bg-orange-600 text-white border-none rounded-lg py-2.5 sm:py-3 text-xs sm:text-sm font-semibold cursor-pointer flex items-center justify-center gap-2 transition-colors"
                        >
                            Fermer
                        </button>
                    </div>
                </div>
            </div>

            {/* Styles d'animation */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
                .animate-slideUp {
                    animation: slideUp 0.3s ease-out;
                }
            `}</style>
        </>
    );
}

export default ResultatComparatifByEleve;
